import React, { useLayoutEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { Box, Drawer, IconButton, Tooltip, useMediaQuery } from "@mui/material";
import Swal from "sweetalert2";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { getDashboardRouteForUser, getNavigationMenuList, getStoredLoginMenus } from "utils/menuConfig";
import { clearCheckoutSessionData, clearOrderBookingDraftData } from "utils/constants";
import logo from "assets/images/logoside.png";

const SIDEBAR_WIDTH = 250;
const COLLAPSED_WIDTH = 72;
const HEADER_HEIGHT = 72;

const SidebarContent = ({ isMobile = false, isCollapsed = false, onClose, scrollStateRef }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const containerRef = useRef(null);
  const currentPath = location.pathname;
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const filteredMenuList = getNavigationMenuList(getStoredLoginMenus(), user);
  const distibutor = JSON.parse(localStorage.getItem("distributor"));
  const retailerName = JSON.parse(localStorage.getItem("retailerName"));
  const fromedit = localStorage.getItem("fromedit");
  const [openSections, setOpenSections] = useState(() => {
    const obj = {};
    filteredMenuList.forEach((sec) => {
      obj[sec.title] = true;
    });
    return obj;
  });
  const parentRoutes = filteredMenuList.flatMap((section) => section.subItems.map((item) => item.nav));
  const showBackButton = !parentRoutes.includes(currentPath);

  const confirmSampleRequestLeave = () =>
    currentPath !== "/sample-request-selection" ||
    window.confirm("If you leave this screen, all entered data will be lost. Do you want to continue?");

  const hideOnPendingOrder =
    currentPath.includes("pending-order-reports") ||
    currentPath.includes("outstanding-reports") ||
    currentPath.includes("non-billed-reports") ||
    currentPath.includes("collection-recieved") ||
    currentPath.includes("collection-target-achv-report");

  const toggleSection = (title) => {
    setOpenSections((prev) => ({
      ...prev,
      [title]: !prev[title]
    }));
  };

  const resetMainContentScroll = () => {
    window.scrollTo(0, 0);
    document.scrollingElement?.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    document.getElementById("root")?.scrollTo?.(0, 0);
    document.querySelector("main")?.scrollTo?.(0, 0);
  };

  const handleMenuToggle = (nextPath) => {
    if (!confirmSampleRequestLeave()) return;

    resetMainContentScroll();

    if (currentPath === "/dashboard" || currentPath === "/report-dashboard") {
      localStorage.setItem("menuNavigationSource", currentPath);
    }

    if (currentPath !== "/report-dashboard") {
      if (currentPath.includes("productBookingg") || currentPath.includes("productBookingf")) {
        showExitAlert(nextPath);
        return;
      }

      if (nextPath.includes("salesorder")) {
        localStorage.setItem("SelectedCustomer", null);
      }

    }

    clearCheckoutSessionData();

    if (nextPath === "/samplecheck") {
      navigate(
        user?.userVerticalId === "Fabric"
          ? "/samplecheckf"
          : "/samplecheck"
      );
      return;
    }

    navigate(nextPath);
  };

  const showExitAlert = async (nextPath) => {
    const swalResult = await Swal.fire({
      title: "Siyaram",
      text: "Do you want to continue without saving current grid value ?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      allowOutsideClick: false,
      customClass: {
        confirmButton: "swal-confirm-button",
        cancelButton: "swal-cancel-button",
        actions: "swal-buttons-container"
      },
      buttonsStyling: false
    });

    if (swalResult.isConfirmed) {
      clearOrderBookingDraftData();
      clearCheckoutSessionData();

      if ((distibutor == "undefined" || distibutor == null) && (retailerName == "undefined" || retailerName == null) && fromedit !== "1") {
        navigate(-1);
      } else {
        if (typeof nextPath === "number") {
          navigate(nextPath);
        } else {
          navigate(nextPath, { replace: true });
        }
      }
    }
  };

  const handleBackButtonClick = () => {
    const storedNavigationSource = localStorage.getItem("menuNavigationSource");
    const menuNavigationSource = !storedNavigationSource || storedNavigationSource === "/report-dashboard"
      ? getDashboardRouteForUser(getStoredLoginMenus(), user)
      : storedNavigationSource;

    if (!confirmSampleRequestLeave()) return;

    if (currentPath === "/sample-request-selection") {
      navigate("/view-sample-request", { replace: true });
      return;
    }

    if (currentPath.includes("productBookingg") || currentPath.includes("productBookingf")) {
      showExitAlert(-1);
      return;
    }

    if (currentPath.includes("salesorder")) {
      localStorage.setItem("SelectedCustomer", null);
      navigate("/dashboard", { replace: true });
      return;
    }

    if (currentPath === "/barcodedashboard") {
      navigate("/salesorder", { replace: true });
      return;
    }

    if (currentPath === "/beats" || currentPath === "/good-returns-list") {
      navigate("/dashboard", { replace: true });
      return;
    }

    if (hideOnPendingOrder) {
      navigate(menuNavigationSource, { replace: true });
      return;
    }

    navigate(-1);
  };

  useLayoutEffect(() => {
    if (containerRef.current && scrollStateRef?.current?.top) {
      containerRef.current.scrollTop = scrollStateRef.current.top;
    }
  }, [isCollapsed, scrollStateRef]);

  return (
    <Box
      ref={containerRef}
      onScroll={(e) => {
        if (scrollStateRef) {
          scrollStateRef.current = { top: e.currentTarget.scrollTop };
        }
      }}
      sx={{
        width: isCollapsed ? COLLAPSED_WIDTH : SIDEBAR_WIDTH,
        height: "100%",
        bgcolor: "#0F172A",
        color: "#E5E7EB",
        overflowY: "auto",
        overflowX: "hidden",
        scrollbarWidth: "thin",
        scrollbarColor: "rgba(148, 163, 184, 0.75) rgba(15, 23, 42, 0.9)",
        "&::-webkit-scrollbar": {
          width: 8
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "rgba(15, 23, 42, 0.9)"
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(148, 163, 184, 0.75)",
          borderRadius: 8,
          border: "2px solid #0F172A"
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: "rgba(203, 213, 225, 0.95)"
        }
      }}
    >
      <Box
        sx={{
          height: `${HEADER_HEIGHT}px`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${theme.palette.divider}`,
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "#0F172A",
          px: isCollapsed ? 0.75 : 1,
          overflow: "visible"
        }}
      >
        <Box
          sx={{
            flex: 1,
             minWidth: 0,
             display: "flex",
             justifyContent: "flex-start",
             pr: isCollapsed ? 0 : 6
           }}
         >
           {!isCollapsed && (
             <img
               src={logo}
               alt="Siyaram"
               width="100%"
               height={64}
               style={{ objectFit: "contain" }}
             />
           )}
        </Box>

        {!isMobile && showBackButton && (
          <IconButton
            onClick={handleBackButtonClick}
            disableRipple
            aria-label="go back"
            sx={{
              position: "fixed",
              top: 15,
              left: (isCollapsed ? COLLAPSED_WIDTH : SIDEBAR_WIDTH) + 10,
              width: 42,
              height: 42,
              color: "#F8FAFC",
              bgcolor: "#7F1D1D",
              border: "1px solid rgba(248,113,113,0.32)",
              borderRadius: "14px",
              boxShadow: "0 10px 24px rgba(2,6,23,0.32)",
              flexShrink: 0,
              p: 0,
              transition: "all 0.2s ease, left 0.2s ease",
              "&:hover": {
                bgcolor: "#991B1B",
                transform: "translateX(-1px)"
              },
              "&::after": {
                content: '""',
                position: "absolute",
                inset: 4,
                borderRadius: "10px",
                border: "1px solid rgba(255,255,255,0.08)"
              },
              "& .MuiSvgIcon-root": {
                fontSize: 18,
                position: "relative",
                zIndex: 1
              }
            }}
          >
            <ArrowBackIosNewIcon />
          </IconButton>
        )}

        {isMobile && (
          <IconButton onClick={onClose} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {filteredMenuList.map((section, index) => (
        <Box key={section.title} sx={{ px: 1, mt: index === 0 ? 3 : 0 }}>
          {!isCollapsed && (
            <Box
              onClick={() => toggleSection(section.title)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1.5,
                py: 1,
                cursor: "pointer",
                color: "#94A3B8",
                textTransform: "uppercase",
                fontSize: 13,
                "&:hover": { color: "#fff" }
              }}
            >
              {section.title}
              {openSections[section.title] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </Box>
          )}

          {(isCollapsed || openSections[section.title]) &&
            section.subItems.map((item) => {
              const isDisabled = Boolean(item.disabled);

              return (
                <Tooltip key={item.name} title={isCollapsed ? item.name : ""} placement="right">
                  <NavLink
                    to={item.nav}
                    onClick={(e) => {
                      e.preventDefault();
                      if (isDisabled) {
                        e.stopPropagation();
                        return;
                      }
                      handleMenuToggle(item.nav);
                      if (isMobile && onClose) onClose();
                    }}
                    style={({ isActive }) => ({
                      display: "flex",
                      alignItems: "center",
                      justifyContent: isCollapsed ? "center" : "flex-start",
                      gap: isCollapsed ? 0 : 10,
                      padding: isCollapsed ? "10px 8px" : "8px 12px",
                      marginBottom: 4,
                      borderRadius: 8,
                      fontSize: 14,
                      textDecoration: "none",
                      color: isDisabled ? "#64748B" : isActive ? "red" : "#CBD5E1",
                      background: !isDisabled && isActive ? "#1E293B" : "transparent",
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      opacity: isDisabled ? 0.55 : 1,
                      pointerEvents: isDisabled ? "none" : "auto"
                    })}
                    aria-disabled={isDisabled || undefined}
                    tabIndex={isDisabled ? -1 : undefined}
                  >
                    {item.image && (
                      <img
                        src={item.image}
                        alt=""
                        width={18}
                        height={18}
                        style={{ objectFit: "contain", flexShrink: 0 }}
                      />
                    )}
                    {!isCollapsed && item.name}
                  </NavLink>
                </Tooltip>
              );
            })}
        </Box>
      ))}
      <Box sx={{ height: 20 }} />
    </Box>
  );
};

const ReportsSidebar = ({ open, setOpen }) => {
  const isMobile = useMediaQuery("(max-width:900px)");
  const scrollStateRef = useRef({ top: 0 });

  if (isMobile) {
    return (
      <>
        {!open && (
          <IconButton
            onClick={() => setOpen(true)}
            disableRipple
            sx={{
              position: "fixed",
              top: 18,
              left: 18,
              zIndex: 1300,
              width: 34,
              height: 34,
              bgcolor: "#111827",
              border: "1px solid #334155",
              borderRadius: "10px",
              color: "#fff",
              boxShadow: "0 6px 14px rgba(0,0,0,0.18)",
              "&:hover": { bgcolor: "#0f172a" },
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                background: "transparent"
              },
              "& .MuiTouchRipple-root": {
                display: "none"
              },
              "& .MuiSvgIcon-root": {
                fontSize: 20
              }
            }}
          >
            <MenuIcon />
          </IconButton>
        )}

        <Drawer open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { width: SIDEBAR_WIDTH } }}>
          <SidebarContent isMobile onClose={() => setOpen(false)} scrollStateRef={scrollStateRef} />
        </Drawer>
      </>
    );
  }

  return (
    <Box
      sx={{
        width: open ? SIDEBAR_WIDTH : COLLAPSED_WIDTH,
        position: "fixed",
        left: 0,
        top: 0,
        height: "100vh",
        transition: "width 0.2s ease",
        zIndex: 1200
      }}
    >
      <SidebarContent isCollapsed={!open} scrollStateRef={scrollStateRef} />
      <Tooltip title={open ? "Collapse Sidebar" : "Expand Sidebar"} placement="right" arrow>
        <IconButton
          onClick={() => setOpen(!open)}
          disableRipple
          aria-label={open ? "collapse sidebar" : "expand sidebar"}
          sx={{
             position: "absolute",
             top: 15,
             right: open ? 10 : 15,
             zIndex: 11,
             width: 42,
            height: 42,
            color: "#F8FAFC",
            bgcolor: "transparent",
            border: "none",
            borderRadius: 0,
            boxShadow: "none",
            transition: "all 0.2s ease",
            "&:hover": {
              bgcolor: "transparent",
              transform: "translateX(1px)"
            },
            "& .MuiSvgIcon-root": {
              fontSize: 20
            }
          }}
        >
          {open ? <KeyboardDoubleArrowLeftIcon /> : <KeyboardDoubleArrowRightIcon />}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default ReportsSidebar;
